import { useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { color, studyStatusTheme, type RoleTone } from '../styles/theme';
import { fullDate } from '../lib/format';
import {
  useAdmins,
  useInviteAdmin,
  useResendInvite,
  useSuspendAdmin,
  useReactivateAdmin,
  useSetPhiMatch,
  usePermissionMatrix,
  type InviteResult,
} from '../api/queries';
import type { Study } from '../api/studies';
import {
  useRoles,
  useStudyMembers,
  useGrantStudy,
  useRevokeStudy,
  isEndpointMissing,
  roleRefusal,
  ONLY_AN_OWNER,
} from '../api/access';
import { request, type AdminRow, type PhiMatch, type StudyMember } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { MemberIdentity, StatusPill } from '../components/MemberIdentity';
import { RolePill, PreviewPill } from '../components/pills';
import { IconChevron } from '../components/icons';
import {
  PageTitle,
  Card,
  CardTitle,
  ColHead,
  Loading,
  ErrorNote,
  Button,
  Field,
  Confirm,
  Eyebrow,
  Empty,
  inputStyle,
  selectStyle,
} from '../components/ui';

/**
 * Users — who can sign in, and which studies each person can see.
 *
 * Roles are set on the Roles & permissions screen; this one is the account
 * itself: the invite, the suspension, and — because every intake form is now
 * one study's — which studies a person is allowed to work in. Owners see every
 * study without being listed; everyone else sees only what is granted here.
 *
 * PHI-Match — who may see, and who may make, a link between a REDCap record
 * and a website record — is per person too, so it is set here, beside the
 * account (QA C-ADM-035). A suspended account comes back with Reactivate
 * (C-ADM-019).
 *
 * The API decides who may hand out what (20 Oct 2026): nobody changes their
 * own account's access, only an owner touches an owner's account or makes an
 * owner, and anyone else gives only the permissions, PHI-Match level and
 * studies they have themself. The controls follow the same rules — what
 * would be refused is greyed out with the reason, never offered and then
 * answered with a 403 — and the API's own sentence shows if it refuses anyway.
 *
 * Pointed at an API older than the study-access routes, the Study access
 * card says it is not connected, lists nobody and disables the assign
 * controls, saying why — it never invents a member. Invites and suspensions
 * still work. See src/api/access.ts.
 */

const USERS_GRID = 'minmax(0,2fr) minmax(0,1.1fr) minmax(0,1.1fr) 64px minmax(0,1fr) 170px';

/** The PHI-Match levels in order, so "above your own" has a meaning. */
const PHI_RANK: Record<PhiMatch, number> = { none: 0, view: 1, match: 2 };

/**
 * GET /studies/ for the Study access card, asked only with studies:read — a
 * role that runs accounts without it was shown a 403 here. The same query as
 * `useStudies` (src/api/studies.ts), sharing its cache key, with the gate.
 */
function useStudyList(enabled: boolean) {
  return useQuery({
    queryKey: ['studies'],
    queryFn: () => request<{ studies: Study[] }>('/studies/'),
    select: (d) => d.studies,
    enabled,
  });
}
const ACCESS_GRID = '120px minmax(0,2fr) minmax(0,1.3fr) 80px 28px';

const selectedRow: CSSProperties = {
  background: color.tint,
  boxShadow: `inset 3px 0 0 ${color.blue}`,
};

const linkButton: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: color.muted,
  fontSize: 12.5,
  fontWeight: 700,
  cursor: 'pointer',
  padding: '6px 8px',
  borderRadius: 8,
};

/**
 * What an invite did, in words. The temporary password is shown only when the
 * email did not go — the API returns it just once, for handing over in person.
 */
function inviteNotice(lead: string, email: string, res: InviteResult): string {
  if (res.inviteEmail === 'sent') {
    return `${lead} ${email}. They have been emailed a temporary password and will choose their own when they first sign in.`;
  }
  const why =
    res.inviteEmail === 'failed' ? 'the invite email could not be sent' : 'no email provider is configured';
  return `${lead} ${email}, but ${why}. Give them this temporary password yourself (it works for 7 days, once): ${res.temporaryPassword ?? ''}`;
}

export function Users() {
  const { admin: me, can } = useAuth();
  const manage = can('admins:manage');
  const meOwner = me?.role === 'owner';
  const readsStudies = can('studies:read');
  const admins = useAdmins();
  const matrix = usePermissionMatrix();
  const studies = useStudyList(readsStudies);
  const roles = useRoles();
  const invite = useInviteAdmin();
  const resend = useResendInvite();
  const suspend = useSuspendAdmin();
  const reactivate = useReactivateAdmin();
  const setPhi = useSetPhiMatch();
  const grant = useGrantStudy();
  const revoke = useRevokeStudy();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmSuspend, setConfirmSuspend] = useState<string | null>(null);
  const [confirmReactivate, setConfirmReactivate] = useState<string | null>(null);
  /** What a reactivation did, under the table — with Resend invite when they came back invited. */
  const [rowNotice, setRowNotice] = useState<{ text: string; invite?: { id: string; email: string } } | null>(
    null,
  );
  /** A PHI-Match level picked from someone's dropdown, waiting for the confirmation. */
  const [pendingPhi, setPendingPhi] = useState<{ id: string; level: PhiMatch } | null>(null);
  /** The study whose people the right-hand panel is showing. */
  const [slug, setSlug] = useState<string | null>(null);
  /** The person picked in the assign control, before Give access is pressed. */
  const [pick, setPick] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  // One probe decides the preview for the whole page: the collection route
  // has no id in it, so its 404 can only mean the routes are missing.
  const preview = isEndpointMissing(roles.error);
  const members = useStudyMembers(slug, !preview);

  if (admins.isLoading) return <Loading />;
  if (admins.error) return <ErrorNote message={(admins.error as Error).message} />;
  if (!admins.data) return null;

  const people = admins.data.admins;
  // The API lists every role an account can hold, built-in and named, in one
  // place; the invite select groups them the way the Roles screen does.
  const fixed = admins.data.roles.filter((r) => r.builtIn);
  const named = admins.data.roles.filter((r) => !r.builtIn);
  const toneOf = (key: string): RoleTone =>
    key === 'owner'
      ? 'owner'
      : key === 'custom'
        ? 'custom'
        : fixed.some((f) => f.key === key)
          ? 'preset'
          : 'named';

  /**
   * Why the invite cannot give a role, or null. With the role sets still
   * loading (or not loaded), only the owner rule is known; the API judges
   * the rest and its sentence shows.
   */
  const inviteRefusal = (key: string): string | null =>
    matrix.data
      ? roleRefusal(me, matrix.data, key)
      : key === 'owner' && !meOwner
        ? 'Only an owner can make someone an owner.'
        : null;
  const roleChoices = [
    ...fixed.filter((f) => f.key !== 'custom'),
    ...named,
    { key: 'custom', label: 'Custom' },
  ];
  // The default (Viewer) may be one this person cannot give: fall back to the first they can.
  const inviteRole =
    roleChoices.some((r) => r.key === role) && !inviteRefusal(role)
      ? role
      : (roleChoices.find((r) => !inviteRefusal(r.key))?.key ?? 'custom');
  const someRefused = roleChoices.some((r) => inviteRefusal(r.key));
  const roleOption = (r: { key: string; label: string }) => (
    <option key={r.key} value={r.key} disabled={!!inviteRefusal(r.key)}>
      {inviteRefusal(r.key) ? `${r.label} (${ONLY_AN_OWNER})` : r.label}
    </option>
  );

  /** My own PHI-Match level: above it, only an owner gives. */
  const myPhi: PhiMatch = people.find((p) => p.id === me?.id)?.phiMatch ?? 'none';
  const levels = admins.data.phiMatchLevels;
  const levelOf = (key: PhiMatch) => levels.find((l) => l.key === key);

  const owners = people.filter((p) => p.role === 'owner');
  const studyRows: Study[] = studies.data ?? [];
  const studyGone = !preview && slug !== null && isEndpointMissing(members.error);
  const granted: StudyMember[] = preview ? [] : (members.data?.members ?? []);
  // Not an owner (they see every study), not the suspended, and not yourself: nobody changes their own access.
  const assignable = people.filter(
    (p) =>
      p.role !== 'owner' &&
      p.status !== 'suspended' &&
      p.id !== me?.id &&
      !granted.some((g) => g.id === p.id),
  );
  /** Anyone but an owner gives or takes away only a study they have themself. */
  const holdsStudy = (key: string): boolean =>
    meOwner || !!me?.allStudies || !!me?.studies.includes(key);
  const openStudy = studyRows.find((s) => s.slug === slug) ?? null;
  const countFor = (s: Study) => people.filter((p) => p.studies.includes(s.slug)).length;

  async function submit() {
    setNotice(null);
    try {
      const res = await invite.mutateAsync({ name, email, role: inviteRole });
      setName('');
      setEmail('');
      setNotice(inviteNotice('Invited', email, res));
    } catch {
      /* surfaced through invite.error */
    }
  }

  async function sendAgain(id: string, address: string) {
    setNotice(null);
    setRowNotice(null);
    try {
      setNotice(inviteNotice('Invited again:', address, await resend.mutateAsync(id)));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      /* surfaced through resend.error */
    }
  }

  async function reactivateOne(a: AdminRow) {
    setRowNotice(null);
    try {
      const status = await reactivate.mutateAsync(a.id);
      setRowNotice(
        status === 'invited'
          ? {
              text: `${a.name} is back, as invited: they never chose a password, so they need a new invite before they can sign in.`,
              invite: { id: a.id, email: a.email },
            }
          : status === 'active'
            ? { text: `${a.name} can sign in again, with the password and the access they had before.` }
            : { text: `${a.name} has been reactivated.` },
      );
    } catch {
      /* surfaced through reactivate.error */
    }
  }

  const assignWhy = preview
    ? 'This API does not have study access yet.'
    : !manage
      ? undefined
      : openStudy && !holdsStudy(openStudy.slug)
        ? `Only an owner can give or take away a study you do not have yourself.`
        : assignable.length === 0
          ? 'Everyone who can be given this study already has it.'
          : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} className="fade-in">
      <PageTitle sub="Who can sign in, and which studies each person can see.">
        User management
      </PageTitle>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,340px),1fr))',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {manage ? (
          <Card pad={24}>
            <CardTitle style={{ marginBottom: 4 }}>Invite someone</CardTitle>
            <p style={{ fontSize: 12.5, color: color.muted, margin: '0 0 14px' }}>
              They are emailed a temporary password that works for 7 days. When they first sign in,
              they choose their own password in its place. Studies are given after they are in.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Field label="Full name">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Sarah Lindqvist"
                  style={inputStyle}
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@enactresearch.com"
                  style={inputStyle}
                />
              </Field>
              <Field label="Role">
                <select value={inviteRole} onChange={(e) => setRole(e.target.value)} style={selectStyle}>
                  <optgroup label="Fixed">{fixed.filter((f) => f.key !== 'custom').map(roleOption)}</optgroup>
                  {named.length > 0 && <optgroup label="Named roles">{named.map(roleOption)}</optgroup>}
                  <optgroup label="One person">{roleOption({ key: 'custom', label: 'Custom' })}</optgroup>
                </select>
              </Field>
              {someRefused && (
                <p style={{ fontSize: 12, color: color.muted, margin: '-4px 0 0' }}>
                  You can give only access you have yourself; a role that includes more, and Owner, is
                  an owner’s to give.
                </p>
              )}
              <Button
                variant="navy"
                onClick={() => void submit()}
                disabled={invite.isPending || !name.trim() || !email.trim()}
                style={{ padding: '12px 22px', fontSize: 14 }}
              >
                {invite.isPending ? 'Sending…' : 'Send invite'}
              </Button>
              {invite.error && <ErrorNote message={(invite.error as Error).message} />}
              {notice && (
                <p
                  style={{
                    background: color.tint,
                    color: color.blueDeep,
                    borderRadius: 12,
                    padding: '12px 14px',
                    fontSize: 12.5,
                    fontWeight: 600,
                    margin: 0,
                    wordBreak: 'break-all',
                  }}
                >
                  {notice}
                </p>
              )}
            </div>
          </Card>
        ) : (
          <Card pad={24}>
            <CardTitle style={{ marginBottom: 4 }}>Invite someone</CardTitle>
            <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
              Only someone who manages accounts can send invites.
            </p>
          </Card>
        )}

        <div
          style={{
            background: color.tint,
            border: `1px solid ${color.tint2}`,
            borderRadius: 16,
            padding: '16px 20px',
          }}
        >
          <Eyebrow tone="blue">Study access</Eyebrow>
          <div style={{ display: 'grid', gap: 6 }}>
            <p style={{ fontSize: 13, lineHeight: '21px', color: color.ink, margin: 0 }}>
              <strong>Owners</strong> see every study and are never listed on one.
            </p>
            <p style={{ fontSize: 13, lineHeight: '21px', color: color.ink, margin: 0 }}>
              <strong>Everyone else</strong> sees only the studies given to them below — the intake
              forms are one study’s each, so this is what decides whose participants they see.
            </p>
            <p style={{ fontSize: 13, lineHeight: '21px', color: color.ink, margin: 0 }}>
              <strong>Records with no study</strong> — general inquiries and requests — stay visible
              to anyone whose role can see records.
            </p>
          </div>
        </div>
      </div>

      {/* ── Users ── */}
      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div style={{ padding: '20px 20px 14px' }}>
          <CardTitle style={{ marginBottom: 4 }}>Users</CardTitle>
          <p style={{ fontSize: 12.5, color: color.muted, margin: 0, maxWidth: 680 }}>
            Suspending someone signs them out now and keeps their record of what they did;
            Reactivate lets them back in with what they had. Roles are changed under Roles &
            permissions.
          </p>
          <p style={{ fontSize: 12.5, color: color.muted, margin: '8px 0 0', maxWidth: 680 }}>
            <strong style={{ color: color.ink }}>PHI-Match</strong> is set per person, never by role:
            whether they may see, or make, a link between a REDCap record and a website record. A
            Developer can never hold it.{' '}
            {levels.map((l, i) => (
              <span key={l.key}>
                <strong style={{ color: color.ink }}>{l.label}</strong> — {l.blurb}
                {i < levels.length - 1 ? ' ' : ''}
              </span>
            ))}
          </p>
        </div>
        <div className="x-scroll">
          <div style={{ minWidth: 900 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: USERS_GRID,
                gap: 12,
                padding: '11px 20px',
                background: color.bg,
              }}
            >
              <ColHead>Member</ColHead>
              <ColHead>Role</ColHead>
              <ColHead>PHI-Match</ColHead>
              <ColHead>Studies</ColHead>
              <ColHead>Last in</ColHead>
              <span />
            </div>

            {people.map((a) => {
              const isMe = a.id === me?.id;
              // Not your own account, and an owner's only if you are one (the API's rules).
              const editable = manage && !isMe && (a.role !== 'owner' || meOwner);
              const count = a.role === 'owner' ? 'All' : preview ? '—' : a.studies.length;
              /** Why this row's PHI-Match is fixed from here, or null when it can be set. */
              const phiWhy =
                a.role === 'developer'
                  ? 'A Developer account can never hold PHI-Match.'
                  : isMe
                    ? 'PHI-Match access cannot be changed on your own account.'
                    : a.role === 'owner' && !meOwner
                      ? 'Only an owner can change an owner’s account.'
                      : null;
              const aboveMine = (key: PhiMatch) => !meOwner && PHI_RANK[key] > PHI_RANK[myPhi];
              const phiPicked = pendingPhi?.id === a.id ? pendingPhi.level : null;
              return (
                <div
                  key={a.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: USERS_GRID,
                    gap: 12,
                    alignItems: 'center',
                    padding: '13px 20px',
                    borderBottom: `1px solid ${color.line}`,
                  }}
                >
                  <MemberIdentity
                    name={a.name}
                    email={a.email}
                    status={a.status}
                    isMe={isMe}
                    lastLoginAt={a.lastLoginAt}
                  />
                  <span style={{ minWidth: 0 }}>
                    <RolePill tone={toneOf(a.role)} label={a.roleLabel} />
                  </span>
                  <span style={{ minWidth: 0 }}>
                    {a.role === 'developer' ? (
                      <span style={{ fontSize: 12.5, color: color.muted }} title={phiWhy ?? undefined}>
                        Not allowed
                      </span>
                    ) : manage && !phiWhy ? (
                      <Confirm
                        open={phiPicked !== null}
                        title={`Set ${a.name}’s PHI-Match to ${levelOf(phiPicked ?? 'none')?.label ?? phiPicked ?? ''}?`}
                        detail={
                          phiPicked
                            ? `${a.phiMatchLabel} → ${levelOf(phiPicked)?.label ?? phiPicked}. ${levelOf(phiPicked)?.blurb ?? ''} It applies from the next thing they do.`
                            : undefined
                        }
                        confirmLabel="Change PHI-Match"
                        onConfirm={() => {
                          if (pendingPhi) setPhi.mutate({ id: pendingPhi.id, phiMatch: pendingPhi.level });
                          setPendingPhi(null);
                        }}
                        onCancel={() => setPendingPhi(null)}
                      >
                        <select
                          // Shows the pick while it waits; back to the stored level on cancel.
                          value={phiPicked ?? a.phiMatch}
                          disabled={setPhi.isPending}
                          aria-label={`${a.name}: PHI-Match`}
                          title={levelOf(a.phiMatch)?.blurb}
                          onChange={(e) => {
                            const level = e.target.value as PhiMatch;
                            setPendingPhi(level !== a.phiMatch ? { id: a.id, level } : null);
                          }}
                          style={{
                            ...selectStyle,
                            borderRadius: 999,
                            padding: '6px 26px 6px 14px',
                            fontSize: 12.5,
                          }}
                        >
                          {levels.map((l) => (
                            <option
                              key={l.key}
                              value={l.key}
                              disabled={l.key !== a.phiMatch && aboveMine(l.key)}
                            >
                              {l.key !== a.phiMatch && aboveMine(l.key)
                                ? `${l.label} (${ONLY_AN_OWNER})`
                                : l.label}
                            </option>
                          ))}
                        </select>
                      </Confirm>
                    ) : (
                      <span
                        style={{ fontSize: 12.5, color: color.ink }}
                        title={[levelOf(a.phiMatch)?.blurb, manage ? phiWhy : null].filter(Boolean).join(' ')}
                      >
                        {a.phiMatchLabel}
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: color.ink,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                    title={a.role === 'owner' ? 'Owners see every study.' : undefined}
                  >
                    {count}
                  </span>
                  <span style={{ fontSize: 12.5, color: color.muted }}>
                    {a.lastLoginAt ? fullDate(a.lastLoginAt) : 'Never'}
                  </span>
                  <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {editable && a.status === 'suspended' && (
                      <Confirm
                        open={confirmReactivate === a.id}
                        title={`Reactivate ${a.name}?`}
                        detail="They can sign in again with the password they had, and get back the role, boxes and studies they had. Someone who never chose a password comes back as invited and needs a new invite."
                        confirmLabel="Reactivate"
                        align="right"
                        onConfirm={() => {
                          setConfirmReactivate(null);
                          void reactivateOne(a);
                        }}
                        onCancel={() => setConfirmReactivate(null)}
                      >
                        <button
                          onClick={() => setConfirmReactivate(confirmReactivate === a.id ? null : a.id)}
                          disabled={reactivate.isPending}
                          className="hov-tint"
                          style={{ ...linkButton, color: color.blue }}
                        >
                          {reactivate.isPending && reactivate.variables === a.id ? 'Reactivating…' : 'Reactivate'}
                        </button>
                      </Confirm>
                    )}
                    {editable && a.status === 'invited' && (
                      <button
                        onClick={() => void sendAgain(a.id, a.email)}
                        disabled={resend.isPending}
                        title="Email a new temporary password, good for another 7 days. The old one stops working."
                        style={{ ...linkButton, marginRight: 14 }}
                      >
                        Resend invite
                      </button>
                    )}
                    {editable && a.status !== 'suspended' && (
                      <Confirm
                        open={confirmSuspend === a.id}
                        title={
                          a.status === 'invited' ? `Revoke ${a.name}’s invite?` : `Suspend ${a.name}?`
                        }
                        detail={
                          a.status === 'invited'
                            ? 'Their temporary password stops working.'
                            : 'They are signed out now and cannot sign back in. What they did stays on the record.'
                        }
                        confirmLabel={a.status === 'invited' ? 'Revoke' : 'Suspend'}
                        align="right"
                        onConfirm={() => {
                          setConfirmSuspend(null);
                          suspend.mutate(a.id);
                        }}
                        onCancel={() => setConfirmSuspend(null)}
                      >
                        <button
                          onClick={() => setConfirmSuspend(confirmSuspend === a.id ? null : a.id)}
                          disabled={suspend.isPending}
                          className="hov-danger"
                          style={linkButton}
                        >
                          {a.status === 'invited' ? 'Revoke invite' : 'Suspend'}
                        </button>
                      </Confirm>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        {rowNotice && (
          <div
            style={{
              margin: '14px 20px',
              background: color.tint,
              color: color.blueDeep,
              borderRadius: 12,
              padding: '12px 14px',
              fontSize: 12.5,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ flex: 1, minWidth: 220 }}>{rowNotice.text}</span>
            {rowNotice.invite && (
              <Button
                variant="soft"
                disabled={resend.isPending}
                title="Email a new temporary password, good for 7 days."
                onClick={() => void sendAgain(rowNotice.invite!.id, rowNotice.invite!.email)}
              >
                {resend.isPending ? 'Sending…' : 'Resend invite'}
              </Button>
            )}
          </div>
        )}
        {suspend.error && (
          <div style={{ padding: '14px 20px' }}>
            <ErrorNote message={(suspend.error as Error).message} />
          </div>
        )}
        {reactivate.error && (
          <div style={{ padding: '14px 20px' }}>
            <ErrorNote message={(reactivate.error as Error).message} />
          </div>
        )}
        {setPhi.error && (
          <div style={{ padding: '14px 20px' }}>
            <ErrorNote message={(setPhi.error as Error).message} />
          </div>
        )}
        {resend.error && (
          <div style={{ padding: '14px 20px' }}>
            <ErrorNote message={(resend.error as Error).message} />
          </div>
        )}
      </Card>

      {/* ── Study access ── */}
      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            flexWrap: 'wrap',
            padding: '20px 20px 14px',
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <CardTitle style={{ marginBottom: 4 }}>Study access</CardTitle>
            <p style={{ fontSize: 12.5, color: color.muted, margin: 0, maxWidth: 680 }}>
              Open a study to see who can work in it, and give or take that access. Owners see every
              study and are not listed.
            </p>
          </div>
          {preview && <PreviewPill />}
        </div>

        {!readsStudies ? (
          <Empty>
            Giving study access needs permission to see the studies. Ask an owner to add it to your
            role.
          </Empty>
        ) : studies.isLoading ? (
          <Loading label="Loading studies…" />
        ) : studies.error ? (
          <div style={{ padding: '0 20px 20px' }}>
            <ErrorNote message={(studies.error as Error).message} />
          </div>
        ) : studyRows.length === 0 ? (
          <Empty>No studies yet. Create one under Studies.</Empty>
        ) : (
          <div className="x-scroll">
            <div style={{ minWidth: 680 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: ACCESS_GRID,
                  gap: 12,
                  padding: '11px 20px',
                  background: color.bg,
                }}
              >
                <ColHead>Code</ColHead>
                <ColHead>Study</ColHead>
                <ColHead>Status</ColHead>
                <ColHead>Assigned</ColHead>
                <span />
              </div>

              {studyRows.map((s) => {
                const open = s.slug === slug;
                const st = studyStatusTheme[s.status];
                const toggle = () => {
                  setSlug(open ? null : s.slug);
                  setPick('');
                  setConfirmRevoke(null);
                };
                return (
                  <div key={s.slug}>
                    {/* The row is the disclosure: one study open at a time, so the
                        list never grows two panels long. */}
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={open}
                      onClick={toggle}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggle();
                        }
                      }}
                      className="hov-row"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: ACCESS_GRID,
                        gap: 12,
                        alignItems: 'center',
                        padding: '12px 20px',
                        borderBottom: `1px solid ${color.line}`,
                        cursor: 'pointer',
                        ...(open ? selectedRow : {}),
                      }}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            fontSize: 11.5,
                            fontWeight: 800,
                            letterSpacing: '0.04em',
                            padding: '4px 9px',
                            borderRadius: 7,
                            background: color.tint,
                            color: color.blueDeep,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontFamily: s.code ? undefined : 'ui-monospace, Menlo, monospace',
                          }}
                        >
                          {s.code || s.slug}
                        </span>
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: color.ink,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {s.label || 'Untitled study'}
                        </span>
                        {!s.published && (
                          <span style={{ display: 'block', fontSize: 11.5, color: color.muted }}>
                            Draft — not on the site
                          </span>
                        )}
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            fontSize: 11,
                            fontWeight: 800,
                            padding: '5px 12px',
                            borderRadius: 999,
                            background: st.bg,
                            color: st.fg,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {st.label}
                        </span>
                      </span>
                      <span
                        style={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: color.ink,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                        title="People given this study. Owners are not counted."
                      >
                        {preview ? '—' : countFor(s)}
                      </span>
                      <span
                        aria-hidden
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          color: color.muted,
                          transform: open ? 'rotate(90deg)' : undefined,
                          transition: 'transform 0.15s ease',
                        }}
                      >
                        <IconChevron size={16} />
                      </span>
                    </div>

                    {open && (
                      <div
                        style={{
                          padding: '14px 20px 18px',
                          borderBottom: `1px solid ${color.line}`,
                          background: color.bg,
                        }}
                      >
                        {studyGone ? (
                          <p style={{ fontSize: 13, color: color.muted, margin: 0 }}>
                            This study no longer exists.
                          </p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 10,
                                flexWrap: 'wrap',
                              }}
                            >
                              <strong style={{ fontSize: 13.5, color: color.navy }}>
                                Who can work in {s.label || 'this study'}
                              </strong>
                              <span style={{ fontSize: 12.5, color: color.muted }}>
                                {granted.length} {granted.length === 1 ? 'person' : 'people'} assigned
                                {owners.length > 0 &&
                                  ` · owners always: ${owners.map((o) => o.name).join(', ')}`}
                              </span>
                            </div>

                            {members.isLoading && !preview ? (
                              <Loading label="Loading who has access…" />
                            ) : members.error && !preview ? (
                              <ErrorNote message={(members.error as Error).message} />
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {granted.map((g) => (
                                  <AccessRow
                                    key={g.id}
                                    person={g}
                                    isMe={g.id === me?.id}
                                    action={
                                      // Not your own access, and only a study you have yourself (the API's rules).
                                      manage && !preview && g.id !== me?.id && holdsStudy(s.slug) ? (
                                        <Confirm
                                          open={confirmRevoke === g.id}
                                          title={`Take ${s.label || 'this study'} away from ${g.name}?`}
                                          detail="They keep their account; they just stop seeing this study’s participants."
                                          confirmLabel="Take away"
                                          align="right"
                                          onConfirm={() => {
                                            setConfirmRevoke(null);
                                            revoke.mutate({ slug: s.slug, adminId: g.id });
                                          }}
                                          onCancel={() => setConfirmRevoke(null)}
                                        >
                                          <button
                                            onClick={() =>
                                              setConfirmRevoke(confirmRevoke === g.id ? null : g.id)
                                            }
                                            disabled={revoke.isPending}
                                            className="hov-danger"
                                            style={linkButton}
                                          >
                                            Unassign
                                          </button>
                                        </Confirm>
                                      ) : null
                                    }
                                  />
                                ))}
                                {granted.length === 0 && (
                                  <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
                                    Nobody is assigned yet. Owners can still see this study.
                                  </p>
                                )}
                              </div>
                            )}

                            {manage && (
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 10,
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                }}
                              >
                                <select
                                  value={pick}
                                  onChange={(e) => setPick(e.target.value)}
                                  disabled={!!assignWhy}
                                  title={assignWhy}
                                  aria-label="Person to give access to"
                                  style={{ ...selectStyle, width: 'auto', minWidth: 240 }}
                                >
                                  <option value="">Pick someone…</option>
                                  {assignable.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.name}
                                      {p.status === 'invited' ? ' (invited)' : ''}
                                    </option>
                                  ))}
                                </select>
                                <Button
                                  variant="soft"
                                  disabled={!!assignWhy || !pick || grant.isPending}
                                  title={assignWhy}
                                  onClick={() => {
                                    grant.mutate({ slug: s.slug, adminId: pick });
                                    setPick('');
                                  }}
                                >
                                  {grant.isPending ? 'Giving…' : 'Give access'}
                                </Button>
                                {assignWhy && (
                                  <span style={{ fontSize: 12, color: color.muted }}>{assignWhy}</span>
                                )}
                                {(grant.error || revoke.error) && (
                                  <ErrorNote
                                    message={((grant.error ?? revoke.error) as Error).message}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/** One line in the access list: who, their role, and how they got here. */
function AccessRow({
  person,
  isMe,
  owner = false,
  action,
}: {
  person: Pick<AdminRow, 'id' | 'name' | 'email' | 'status' | 'roleLabel'>;
  isMe: boolean;
  owner?: boolean;
  action?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 12,
        border: `1px solid ${color.line}`,
        opacity: owner ? 0.6 : 1,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <MemberIdentity name={person.name} email={person.email} status={person.status} isMe={isMe} />
      </span>
      {owner ? (
        <RolePill tone="owner" label="Owner — every study" />
      ) : (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {person.status !== 'active' && <StatusPill status={person.status} />}
          <span style={{ fontSize: 12, color: color.muted, whiteSpace: 'nowrap' }}>
            {person.roleLabel}
          </span>
          {action}
        </span>
      )}
    </div>
  );
}
